import cart from "./cart";
import store from "./store";
import { nextTick } from "vue";
import {
  collectionProductIdSet,
  GWP_MAX_LINE_QUANTITY,
  getGwpConfigs,
  giftLinesForConfig,
  gwpConfigKey,
  isFreeProductProperty,
  isGwpCustomerExcluded,
  totalGiftQuantity,
  type GwpConfigEntry,
} from "./gwp-config";

function qualifyingQuantity(
  cartItems: any[],
  giftVariantId: string,
  collectionProductIds: Set<string>
): number {
  return cartItems.reduce((sum, item) => {
    if (!item || String(item.variant_id) === giftVariantId) {
      return sum;
    }
    if (!collectionProductIds.has(String(item.product_id))) {
      return sum;
    }
    return sum + (Number(item.quantity) || 0);
  }, 0);
}

function parseTrigger(cfg: GwpConfigEntry): number {
  const trigger = parseInt(String(cfg.quantity_trigger ?? "1"), 10);
  if (!Number.isFinite(trigger) || trigger < 1) {
    return 1;
  }
  return trigger;
}

async function changeLineQuantity(
  lineKey: string,
  quantity: number
): Promise<any> {
  const res = await fetch("/cart/change.js", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: lineKey,
      quantity,
    }),
  });

  return res.json();
}

async function removeGiftLines(lines: any[]): Promise<any | null> {
  let lastCart: any = null;

  for (const line of lines) {
    if (!line?.key) {
      continue;
    }

    lastCart = await changeLineQuantity(line.key, 0);
    if (lastCart?.errors || lastCart?.status === 422 || lastCart?.message) {
      return null;
    }
  }

  return lastCart;
}

async function enforceSingleGiftLine(
  cfg: GwpConfigEntry,
  lines: any[]
): Promise<any | null> {
  if (lines.length === 0) {
    return null;
  }

  const [primaryLine, ...extraLines] = lines;
  let lastCart: any = null;

  if (extraLines.length > 0) {
    lastCart = await removeGiftLines(extraLines);
    if (lastCart == null) {
      return null;
    }
  }

  const primaryQty = Number(primaryLine.quantity) || 0;
  if (primaryQty !== GWP_MAX_LINE_QUANTITY && primaryLine.key) {
    lastCart = await changeLineQuantity(
      primaryLine.key,
      GWP_MAX_LINE_QUANTITY
    );
    if (lastCart?.errors || lastCart?.status === 422 || lastCart?.message) {
      return null;
    }
  }

  return lastCart;
}

const addingConfigKeys = new Set<string>();

async function syncOneConfig(
  cfg: GwpConfigEntry,
  cartItems: any[]
): Promise<boolean> {
  const giftVid = String(cfg.variant_id);
  const configKey = gwpConfigKey(cfg);
  const trigger = parseTrigger(cfg);
  const collectionIds = collectionProductIdSet(cfg);

  if (collectionIds.size === 0) {
    return false;
  }

  const giftLines = giftLinesForConfig(cartItems, cfg);
  const giftQty = totalGiftQuantity(giftLines);
  const qualifying = qualifyingQuantity(cartItems, giftVid, collectionIds);

  if (qualifying >= trigger) {
    if (giftQty === 0) {
      if (addingConfigKeys.has(configKey)) {
        return false;
      }

      addingConfigKeys.add(configKey);

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: [
              {
                id: giftVid,
                quantity: GWP_MAX_LINE_QUANTITY,
                properties: {
                  "_free-product": "1",
                  "_gwp_config_id": configKey,
                },
              },
            ],
          }),
        }).then((r) => r.json());

        if (res?.errors || res?.status === 422 || res?.message) {
          return false;
        }

        const fresh = await cart.get();
        if (!fresh?.items) {
          return true;
        }

        const updatedLines = giftLinesForConfig(fresh.items, cfg);
        const updatedQty = totalGiftQuantity(updatedLines);

        if (updatedQty > GWP_MAX_LINE_QUANTITY || updatedLines.length > 1) {
          const reconciled = await enforceSingleGiftLine(cfg, updatedLines);
          return reconciled != null || updatedQty > 0;
        }

        return true;
      } finally {
        addingConfigKeys.delete(configKey);
      }
    }

    if (giftQty > GWP_MAX_LINE_QUANTITY || giftLines.length > 1) {
      const data = await enforceSingleGiftLine(cfg, giftLines);
      return data != null;
    }

    return false;
  }

  if (giftQty > 0) {
    const data = await removeGiftLines(giftLines);
    return data != null;
  }

  return false;
}

let debounceId: ReturnType<typeof setTimeout> | null = null;
let running = false;
let needsFollowUpSync = false;

export function scheduleGiftWithPurchaseSync(): void {
  if (debounceId) {
    clearTimeout(debounceId);
  }
  debounceId = setTimeout(() => {
    debounceId = null;
    void runGiftWithPurchaseSync();
  }, 120);
}

export async function runGiftWithPurchaseSync(): Promise<void> {
  if (isGwpCustomerExcluded()) {
    const cartData = await cart.get();
    if (!cartData || !Array.isArray(cartData.items)) {
      return;
    }

    const giftLines = cartData.items.filter((item: any) =>
      isFreeProductProperty(item?.properties?.["_free-product"])
    );

    if (giftLines.length === 0) {
      return;
    }

    const updatedCart = await removeGiftLines(giftLines);
    if (updatedCart) {
      store.refreshCart(updatedCart, { skipGwp: true });
      await nextTick();
      store.updateLastAdd(Date.now());
    }

    return;
  }

  const configs = getGwpConfigs();
  if (configs.length === 0) {
    return;
  }

  if (running) {
    needsFollowUpSync = true;
    return;
  }
  running = true;
  needsFollowUpSync = false;

  try {
    let cartData = await cart.get();
    if (!cartData || !Array.isArray(cartData.items)) {
      return;
    }

    let anyChanged = false;

    for (const cfg of configs) {
      const changed = await syncOneConfig(cfg, cartData.items);
      if (changed) {
        anyChanged = true;
        cartData = await cart.get();
        if (!cartData || !Array.isArray(cartData.items)) {
          break;
        }
      }
    }

    if (anyChanged && cartData) {
      store.refreshCart(cartData, { skipGwp: true });
      await nextTick();
      store.updateLastAdd(Date.now());
    }
  } finally {
    running = false;
    if (needsFollowUpSync) {
      needsFollowUpSync = false;
      scheduleGiftWithPurchaseSync();
    }
  }
}

export function initGiftWithPurchase(): void {
  document.addEventListener("product:added", () => {
    scheduleGiftWithPurchaseSync();
  });
  void runGiftWithPurchaseSync();
}
